import { OrderType, useSearchAvailableExamCenterManagerUsersQuery } from '@exam-center/graphql';
import {
  Alert,
  Flex,
  type GridRowSelectionModel,
  NoRowsOverlay,
  SearchInput,
  Text,
} from '@shared/components';
import { useMemo, useState } from 'react';

import { BaseAddExamCenterMemberDataGrid } from '../../components';

import { columns } from './columns';
import mapUserForExamCenterData from './mapper';

interface AddExamCenterMemberListProps {
  onSelectRow?: (selectedMembers: string[]) => void;
}

export default function AddExamCenterMemberList(props: Readonly<AddExamCenterMemberListProps>) {
  const { onSelectRow } = props;

  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedMembersCount, setSelectedMembersCount] = useState(0);

  const { data: availableExamCenterManagerUsersData, loading: isSearchingUsers } =
    useSearchAvailableExamCenterManagerUsersQuery({
      variables: {
        searchAvailableExamCenterManagerUsersInput: {
          filter: {
            searchText: {
              fields: ['firstname', 'lastname', 'email'],
              value: searchInput,
            },
          },
          paging: {
            currentPage: page,
            pageSize: pageSize,
          },
          order: {
            field: 'firstname',
            type: OrderType.Asc,
          },
        },
      },
      fetchPolicy: 'network-only',
    });

  const { usersForExamCenter, total } = useMemo(() => {
    const { users, total } =
      availableExamCenterManagerUsersData?.ecSearchAvailableExamCenterManagerUsers ?? {
        users: [],
        total: 0,
      };

    return {
      usersForExamCenter: users.map(mapUserForExamCenterData),
      total,
    };
  }, [availableExamCenterManagerUsersData]);

  const handleRowSelectionChange = (rowSelectionModel: GridRowSelectionModel) => {
    const selectedMembers = rowSelectionModel as string[];
    setSelectedMembersCount(selectedMembers.length);
    onSelectRow?.(selectedMembers);
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    setPage(0);
  };

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
      <Alert
        severity='info'
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Text>{`รายชื่อสมาชิกที่แสดงมาจากระบบ User Management หากไม่พบชื่อผู้ใช้ที่ต้องการ\nกรุณาเพิ่มรายชื่อที่ระบบ User Management โดยกำหนด Role เป็น “ผู้จัดการศูนย์ทดสอบ”`}</Text>
      </Alert>
      <SearchInput placeholder='ค้นหาชื่อหรืออีเมล...' onChange={handleSearchInputChange} />
      <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
        <Text variant='h4'>เลือกสมาชิก ({selectedMembersCount})</Text>
        <Flex
          sx={{
            height: '444px',
          }}
        >
          <BaseAddExamCenterMemberDataGrid
            autoHeight={false}
            columns={columns}
            rows={usersForExamCenter}
            loading={isSearchingUsers}
            hideFooter={isSearchingUsers || total === 0}
            onRowSelectionModelChange={handleRowSelectionChange}
            slots={{
              noRowsOverlay: NoRowsOverlay,
            }}
            slotProps={{
              footer: {
                total: total,
                pagination: {
                  page: page,
                  count: total,
                  rowsPerPage: pageSize,
                  onPageChange: (_event, page) => {
                    setPage(page);
                  },
                  onRowsPerPageChange: (event) => {
                    const pageSize = parseInt(event.target.value);
                    setPage(0);
                    return setPageSize(pageSize);
                  },
                },
              },
            }}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}
